/**
 * Turns a musical step into sound.
 *
 * The engine walks a musical clock and asks this module to voice each
 * sixteenth note. Everything here is a pure function of the step index and
 * the track, which is what makes seeking and scratching behave like a file.
 */

import type { DemoTrack } from '../../types/demo.ts';
import { layers, mtof, STEPS_PER_BAR } from './arrangement.ts';
import { hat, kick, snare } from './drums.ts';
import type { VoiceBus } from './graph.ts';
import { bass, bell, pad, pluck, saw } from './tones.ts';

export interface StepContext {
  bus: VoiceBus;
  track: DemoTrack;
  /** Absolute AudioContext time the step should sound at. */
  when: number;
  /** Real seconds a step lasts, after the pitch fader's rate. */
  stepSeconds: number;
  /** Playback rate — a turntable shifts pitch and tempo together. */
  rate: number;
}

const ARP_VOICES = { pluck, saw, pad: bell } as const;

/** Voice one sixteenth note. Steps already in the past are dropped. */
export function emitStep(step: number, ctx: StepContext): void {
  const { bus, track, when, stepSeconds, rate } = ctx;
  if (when < bus.ctx.currentTime - 0.05) return;

  const s = ((step % STEPS_PER_BAR) + STEPS_PER_BAR) % STEPS_PER_BAR;
  const bar = Math.floor(step / STEPS_PER_BAR);
  const chord = track.prog[bar % track.prog.length] as readonly number[];
  const on = layers(bar);

  if (on.drums && track.kick[s]) kick(bus, when, 0.9);
  if (on.drums && track.snare[s]) snare(bus, when, 0.6);
  if (on.hats && track.hat[s]) hat(bus, when, s % 4 === 2 ? 0.26 : 0.17, s % 8 === 6);

  if (on.bass && track.bass[s]) {
    bass(bus, when, mtof(track.root - 12 + (chord[0] as number)) * rate, stepSeconds * 2.6);
  }

  if (s === 0) {
    const freqs = chord.map((c) => mtof(track.root + c) * rate);
    pad(bus, when, freqs, stepSeconds * 15.4, on.arp ? 0.14 : 0.2);
  }

  if (on.arp && track.arp[s]) {
    const idx = (step * 3 + Math.floor(step / 5)) % chord.length;
    const oct = s % 8 === 0 ? 12 : s % 5 === 0 ? 24 : 12;
    const f = mtof(track.root + (chord[idx] as number) + oct) * rate;
    const voice = ARP_VOICES[track.style];
    voice(
      bus,
      when,
      f,
      stepSeconds * (track.style === 'pluck' ? 2.4 : track.style === 'saw' ? 1.6 : 5),
    );
  }

  if (on.ride && s % 4 === 0) hat(bus, when, 0.1, true);
}
